import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Tag, Button, Input, Select, Empty, Popconfirm } from 'antd';
import { CodeSandboxOutlined, StopOutlined, FileTextOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { WorkerReportWithContext } from '@src/api/deployment/deploymentApi';
import deploymentApi from '@src/api/deployment/deploymentApi';
import useTranslation from '@hooks/useTranslation';
import useKemuNotification from '@common/hooks/useKemuNotification';
import { ReactComponent as ServerIconSvg } from '@src/assets/img/interface/server.svg';
import styles from './ManageEnvironmentsModal.module.css';

const { Search } = Input;

type ProcessesTabProps = {
	onViewLogs: (worker: WorkerReportWithContext) => void;
};

/**
 * Processes tab component displaying all processes in a table with filtering
 */
const ProcessesTab: React.FC<ProcessesTabProps> = ({ onViewLogs }) => {
	const t = useTranslation('ManageEnvironmentsModal');
	const tCommon = t.withBaseKey('CommonWords');
	const showNotification = useKemuNotification();

	const [processes, setProcesses] = useState<WorkerReportWithContext[]>([]);
	const [loading, setLoading] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [environmentFilter, setEnvironmentFilter] = useState<string>('all');
	const [recipeFilter, setRecipeFilter] = useState<string>('all');
	const [statusFilter, setStatusFilter] = useState<string>('all');

	/**
	 * Loads all processes from the API
	 */
	const loadProcesses = useCallback(async () => {
		setLoading(true);
		try {
			const data = await deploymentApi.listAllProcesses();
			setProcesses(data);
		} catch (err) {
			console.error('Failed to load processes:', err);
			showNotification({
				type: 'error',
				message: t('Processes.Error.LoadFailed'),
			});
		} finally {
			setLoading(false);
		}
	}, [t, showNotification]);

	/**
	 * Handles killing a worker process
	 * @param worker - Worker to kill
	 */
	const handleKillWorker = useCallback(async (worker: WorkerReportWithContext) => {
		try {
			const response = await deploymentApi.restartWorker({
				deploymentId: worker.deploymentId,
				hostId: worker.hostId,
				workerId: worker.workerId,
			});

			showNotification({
				type: 'success',
				message: response.message || t('KillWorker.Success'),
			});
			// Reload processes to get updated status
			loadProcesses();
		} catch (err) {
			console.error('Failed to kill worker:', err);
			showNotification({
				type: 'error',
				message: t('KillWorker.Error'),
			});
		}
	}, [showNotification, t, loadProcesses]);

	/**
	 * Gets worker status display info
	 * @param state - Worker state
	 * @returns status text, color, and icon
	 */
	const getWorkerStatus = (state: WorkerReportWithContext['state']): { text: string; color: string; icon: React.ReactNode } => {
		switch (state) {
			case 'running':
				return { text: 'RUNNING', color: 'green', icon: <CheckCircleOutlined /> };
			case 'starting':
				return { text: 'STARTING', color: 'blue', icon: null };
			case 'failed':
				return { text: 'FAILED', color: 'red', icon: <CloseCircleOutlined /> };
			case 'stopped':
				return { text: 'STOPPED', color: 'default', icon: null };
			default:
				return { text: (state as string).toUpperCase(), color: 'default', icon: null };
		}
	};

	/**
	 * Formats a date string for display
	 * @param dateString - ISO date string
	 * @returns formatted date/time string
	 */
	const formatDate = useCallback((dateString: string): string => {
		try {
			return new Date(dateString).toLocaleString();
		} catch {
			return dateString;
		}
	}, []);

	/**
	 * Gets unique environments from processes
	 */
	const uniqueEnvironments = useMemo(() => {
		const envMap = new Map<string, { id: string; name: string }>();
		processes.forEach(p => {
			if (!envMap.has(p.environmentId)) {
				envMap.set(p.environmentId, {
					id: p.environmentId,
					name: p.environmentName,
				});
			}
		});
		return Array.from(envMap.values());
	}, [processes]);

	/**
	 * Gets unique recipes from processes
	 */
	const uniqueRecipes = useMemo(() => {
		const recipeMap = new Map<string, string>();
		processes.forEach(p => {
			const recipeName = p.recipeName;
			if (!recipeMap.has(recipeName)) {
				recipeMap.set(recipeName, recipeName);
			}
		});
		return Array.from(recipeMap.values()).sort();
	}, [processes]);

	/**
	 * Filters processes based on search text and filters
	 */
	const filteredProcesses = useMemo(() => {
		return processes.filter(process => {
			// Search filter
			if (searchText) {
				const searchLower = searchText.toLowerCase();
				const matchesSearch = (
					process.pid.toString().includes(searchLower) ||
					process.recipeName.toLowerCase().includes(searchLower) ||
					process.environmentName.toLowerCase().includes(searchLower) ||
					process.hostName.toLowerCase().includes(searchLower) ||
					(process.hostIpAddress && process.hostIpAddress.toLowerCase().includes(searchLower))
				);
				if (!matchesSearch) return false;
			}

			// Environment filter
			if (environmentFilter !== 'all') {
				if (environmentFilter !== process.environmentId) return false;
			}

			// Recipe filter
			if (recipeFilter !== 'all') {
				if (recipeFilter !== process.recipeName) return false;
			}

			// Status filter
			if (statusFilter !== 'all') {
				if (statusFilter !== process.state) return false;
			}

			return true;
		});
	}, [processes, searchText, environmentFilter, recipeFilter, statusFilter]);

	// Load processes when component mounts
	useEffect(() => {
		loadProcesses();
	}, [loadProcesses]);

	/**
	 * Table columns definition
	 */
	const columns = [
		{
			title: tCommon('Pid'),
			key: 'pid',
			dataIndex: 'pid',
			width: 100,
			render: (pid: number) => pid || '-',
		},
		{
			title: tCommon('Recipe'),
			key: 'recipe',
			render: (_: unknown, record: WorkerReportWithContext) => (
				<div className={styles.ProcessTableRecipe}>
					<CodeSandboxOutlined className={styles.ProcessTableIcon} />
					<div className={styles.ProcessTableRecipeInfo}>
						<span>{record.recipeName}</span>
						{record.recipeVersion && (
							<Tag className={styles.ProcessTableVersion}>{`v${record.recipeVersion}`}</Tag>
						)}
					</div>
				</div>
			),
		},
		{
			title: tCommon('Environment'),
			key: 'environment',
			dataIndex: 'environmentName',
		},
		{
			title: tCommon('Host'),
			key: 'host',
			render: (_: unknown, record: WorkerReportWithContext) => (
				<div className={styles.ProcessTableHost}>
					<ServerIconSvg className={styles.ProcessTableIcon} />
					<span>{record.hostName}</span>
				</div>
			),
		},
		{
			title: tCommon('Status'),
			width: 130,
			key: 'status',
			render: (_: unknown, record: WorkerReportWithContext) => {
				const status = getWorkerStatus(record.state);
				return (
					<Tag color={status.color} icon={status.icon}>
						{status.text}
					</Tag>
				);
			},
		},
		{
			title: tCommon('Started'),
			key: 'started',
			render: (_: unknown, record: WorkerReportWithContext) => {
				return formatDate(record.startedAt);
			},
		},
		{
			title: tCommon('Actions'),
			key: 'actions',
			width: 160,
			render: (_: unknown, record: WorkerReportWithContext) => {
				const isRunning = record.state === 'running';

				return (
					<div className={styles.ProcessTableActions}>
						<Button
							type="text"
							size="small"
							icon={<FileTextOutlined />}
							onClick={() => onViewLogs(record)}
							disabled={!record.workerId}
						>
							{t('ViewLogs')}
						</Button>
						{isRunning && (
							<Popconfirm
								title={t('KillConfirm.Title')}
								description={t('KillConfirm.Description', { pid: record.pid })}
								onConfirm={() => handleKillWorker(record)}
								okText={t('KillConfirm.Confirm')}
								cancelText={t('KillConfirm.Cancel')}
								okButtonProps={{ danger: true }}
							>
								<Button
									type="text"
									size="small"
									icon={<StopOutlined />}
									danger
								>
									{t('Kill')}
								</Button>
							</Popconfirm>
						)}
					</div>
				);
			},
		},
	];

	return (
		<>
			<div className={styles.ProcessesFiltersContainer}>
				<Search
					placeholder={t('Processes.SearchPlaceholder')}
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
					allowClear
					className={styles.ProcessesSearch}
				/>
				<Select
					value={environmentFilter}
					onChange={setEnvironmentFilter}
					className={styles.ProcessesFilter}
					placeholder={tCommon('Environment')}
					popupClassName={styles.SelectDropdown}
				>
					<Select.Option value="all">{tCommon('AllEnvironments')}</Select.Option>
					{uniqueEnvironments.map(env => (
						<Select.Option key={env.id} value={env.id}>
							{env.name}
						</Select.Option>
					))}
				</Select>
				<Select
					value={recipeFilter}
					onChange={setRecipeFilter}
					className={styles.ProcessesFilter}
					placeholder={tCommon('Recipe')}
					popupClassName={styles.SelectDropdown}
				>
					<Select.Option value="all">{tCommon('AllRecipes')}</Select.Option>
					{uniqueRecipes.map(recipe => (
						<Select.Option key={recipe} value={recipe}>
							{recipe}
						</Select.Option>
					))}
				</Select>
				<Select
					value={statusFilter}
					onChange={setStatusFilter}
					className={styles.ProcessesFilter}
					placeholder={tCommon('Status')}
					popupClassName={styles.SelectDropdown}
				>
					<Select.Option value="all">{tCommon('AllStatuses')}</Select.Option>
					<Select.Option value="running">{tCommon('Running')}</Select.Option>
					<Select.Option value="starting">{tCommon('Starting')}</Select.Option>
					<Select.Option value="failed">{tCommon('Failed')}</Select.Option>
					<Select.Option value="stopped">{tCommon('Stopped')}</Select.Option>
				</Select>
			</div>
			<Table
				columns={columns}
				dataSource={filteredProcesses}
				loading={loading}
				rowKey="workerId"
				pagination={{
					pageSize: 10,
					showSizeChanger: true,
					showTotal: (total) => t('Processes.Table.Total', { total }),
				}}
				locale={{
					emptyText: <Empty description={t('Processes.NoProcesses')} />,
				}}
				scroll={{ y: 400 }}
			/>
		</>
	);
};

export default ProcessesTab;

